import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Ticket, Loader2, X } from "lucide-react";
import api from "../api";
import { useSuccessFailure } from "../context/SuccessFailureContext";
import { formatMoney, currencySymbol } from "../utils/format";

const EMPTY = { name: "", price: "", quantity: "", description: "", sales_start: "", sales_end: "", is_active: true };

const TicketTiersManager = ({ event }) => {
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const currency = event?.currency || "KES";
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/events/${event.id}/ticket-tiers/`);
      setTiers(res.data?.results || res.data || []);
    } catch (e) {
      showFailureModal("Error", "Could not load ticket tiers.");
    } finally {
      setLoading(false);
    }
  }, [event?.id, showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (t) => {
    setForm({
      name: t.name || "", price: t.price ?? "", quantity: t.quantity ?? "",
      description: t.description || "",
      sales_start: t.sales_start ? t.sales_start.slice(0, 16) : "",
      sales_end: t.sales_end ? t.sales_end.slice(0, 16) : "",
      is_active: t.is_active !== false,
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showFailureModal("Missing name", "Please give the tier a name.");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      price: parseFloat(form.price) || 0,
      quantity: form.quantity === "" ? null : parseInt(form.quantity, 10),
      description: form.description,
      sales_start: form.sales_start || null,
      sales_end: form.sales_end || null,
      is_active: form.is_active,
    };
    try {
      if (editingId) {
        await api.patch(`/events/${event.id}/ticket-tiers/${editingId}/`, payload);
        showSuccessModal("Tier updated", `"${payload.name}" was updated.`);
      } else {
        await api.post(`/events/${event.id}/ticket-tiers/`, payload);
        showSuccessModal("Tier created", `"${payload.name}" is now on sale.`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const data = err.response?.data;
      const msg = data ? Object.values(data).flat().join(" ") : "Could not save the tier.";
      showFailureModal("Save failed", msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" tier? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${event.id}/ticket-tiers/${t.id}/`);
      await load();
    } catch (e) {
      showFailureModal("Delete failed", "Could not delete this tier. It may have payments attached.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Ticket Tiers</h2>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
          <Plus size={16} /> Add Tier
        </button>
      </div>

      <p className="text-sm text-gray-500">
        Create tiers like Early Bird, Regular, or VIP — each with its own price, optional quantity cap, and sales window.
      </p>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg text-gray-500">
          No tiers yet. Add one to start selling differentiated tickets.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left">Tier</th>
                <th className="px-4 py-3 text-left">Price</th>
                <th className="px-4 py-3 text-left">Sold / Cap</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 text-gray-700 dark:text-gray-200">
              {tiers.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{t.name}</div>
                    {t.description && <div className="text-xs text-gray-500 max-w-xs truncate">{t.description}</div>}
                  </td>
                  <td className="px-4 py-3 font-medium">{formatMoney(t.price, currency)}</td>
                  <td className="px-4 py-3">{t.sold ?? 0} / {t.quantity ?? "∞"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      t.sold_out ? "bg-red-100 text-red-700"
                      : t.on_sale ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                    }`}>
                      {t.sold_out ? "Sold out" : t.on_sale ? "On sale" : "Off sale"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(t)} className="text-gray-500 hover:text-blue-600 mr-3"><Pencil size={16} /></button>
                    <button onClick={() => remove(t)} className="text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingId ? "Edit Tier" : "New Tier"}</h3>
              <button onClick={() => setShowForm(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input name="name" value={form.name} onChange={change}
                  placeholder="e.g. VIP" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ({currencySymbol(currency)}) *</label>
                  <input name="price" type="number" min="0" step="1" value={form.price} onChange={change}
                    placeholder="0" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input name="quantity" type="number" min="1" step="1" value={form.quantity} onChange={change}
                    placeholder="Unlimited" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" rows={2} value={form.description} onChange={change}
                  placeholder="What's included" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales start</label>
                  <input name="sales_start" type="datetime-local" value={form.sales_start} onChange={change}
                    className="w-full p-2 border rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales end</label>
                  <input name="sales_end" type="datetime-local" value={form.sales_end} onChange={change}
                    className="w-full p-2 border rounded-md text-sm" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input name="is_active" type="checkbox" checked={form.is_active} onChange={change} />
                Active (available for purchase)
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2 border rounded-md text-gray-700">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2 rounded-md bg-blue-600 text-white font-medium disabled:opacity-50">
                  {saving ? "Saving…" : editingId ? "Save changes" : "Create tier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketTiersManager;
